// The Briefing's ATTACHMENTS entry (busy-my-web#277).
//
//   📎  12 attachments                     ← the row, in the strip, like every other count row
//       [Documents 3] [PDFs 5] [Images 4]  ← the chips it opens, one per class
//       insurance-policy.pdf   1.2 MB   12 Aug
//       quote.pdf              340 KB   9 Aug
//
// Attachments were the one thing in Busy that the Briefing could not see. A file arrived on an
// email, got a chip under whichever entity it was filed against, and then existed only in the
// `/files/attachments` explorer — a flat, unclassified list. So "where is the insurance PDF" had no
// answer anywhere in the box, and the strip that describes what a story is MADE OF was silent about
// its documents.
//
// This module is the whole rule set for that entry, and it is a plain value module on purpose (like
// lib/briefings/*): rows, labels, glyphs, chip order and the list shape, with no store, no fetch and
// no component. The server owns CLASSIFICATION — busy-document-srv decides what a file is once it
// has read the text, because "invoice" and "receipt" are words inside the document, not guesses off
// its name. What is here is the reading-back of that decision, and one honest fallback for rows the
// server has not classified yet (see `attachmentClass`).

import { fileExtension } from '@/lib/briefings/entityFiles'
import { isImageAttachment } from '@/lib/globalTimelineFeed'
import { countLabel } from '@/lib/briefings/rows'

/** The class slugs, spelled exactly as busy-document-srv and busy-main-bff spell them. */
export const ATTACHMENT_CLASS = {
  document: 'document',
  pdf: 'pdf',
  image: 'image',
  invoice: 'invoice',
  receipt: 'receipt',
  spreadsheet: 'spreadsheet',
  presentation: 'presentation',
  other: 'other',
}

/**
 * The chip row, in the order the design draws it: Documents · PDFs · Images · Invoices · Receipts ·
 * Spreadsheets.
 *
 * `countKey` is the field the BFF's briefing counts use, so the chip and the number over it can
 * never come from two different readings of the same answer.
 *
 * Presentations are LAST and are not in the design's list. They are still here because the count
 * they belong to is the row's total: a user whose only attachments are slide decks would otherwise
 * read "3 attachments" over a chip row that offered nothing to open, which is a dead end rather
 * than a simplification. Like every other chip it is dropped when its count is 0 (`attachmentChips`),
 * so on the design's own data the row is exactly the six chips it asks for.
 *
 * "Other" gets no chip at all: it is not a kind of thing, it is the absence of one, and a chip
 * labelled Other would invite a click into a list nobody can describe. Those files are still in the
 * total, and still in the unfiltered list.
 */
export const ATTACHMENT_CHIPS = [
  { key: ATTACHMENT_CLASS.document, label: 'Documents', countKey: 'documents', icon: 'file-doc' },
  { key: ATTACHMENT_CLASS.pdf, label: 'PDFs', countKey: 'pdfs', icon: 'file-1' },
  { key: ATTACHMENT_CLASS.image, label: 'Images', countKey: 'images', icon: 'gallery' },
  // A bill and a settled bill: the same document at two moments, so the glyphs are the receipt and
  // the tick — the pair the rest of the app already uses for "asked for" and "done".
  { key: ATTACHMENT_CLASS.invoice, label: 'Invoices', countKey: 'invoices', icon: 'receipt-text' },
  { key: ATTACHMENT_CLASS.receipt, label: 'Receipts', countKey: 'receipts', icon: 'completed' },
  { key: ATTACHMENT_CLASS.spreadsheet, label: 'Spreadsheets', countKey: 'spreadsheets', icon: 'chart' },
  { key: ATTACHMENT_CLASS.presentation, label: 'Presentations', countKey: 'presentations', icon: 'file' },
]

/** The strip's own glyph for the entry — the paperclip the frames use for a file. */
export const ATTACHMENTS_ICON = 'attachments'

/** The filter name the Briefing row carries, and the one the store toggles on. */
export const ATTACHMENTS_FILTER = 'attachments'

const CHIP_BY_KEY = ATTACHMENT_CHIPS.reduce((acc, c) => ({ ...acc, [c.key]: c }), {})

/** A class's own word, singular-ish, for a breadcrumb or a heading. '' when it is not one of ours. */
export function attachmentClassLabel(key) {
  return CHIP_BY_KEY[String(key || '').toLowerCase()]?.label || ''
}

/** …and its glyph, falling back to the entry's own paperclip. */
export function attachmentClassIcon(key) {
  return CHIP_BY_KEY[String(key || '').toLowerCase()]?.icon || ATTACHMENTS_ICON
}

/**
 * A class named however it arrived — the BFF sends doc-srv's ENUM NAME ("Pdf"), the chips use the
 * slug ("pdf"), and a plural is what a person would type. Null when the word is not one of ours,
 * which every caller reads as "no narrowing" rather than as "narrow to nothing".
 */
export function parseAttachmentClass(value) {
  const word = String(value || '').trim().toLowerCase().replace(/s$/, '')
  switch (word) {
    case 'pdf': return ATTACHMENT_CLASS.pdf
    case 'image': case 'photo': case 'picture': return ATTACHMENT_CLASS.image
    case 'invoice': case 'bill': return ATTACHMENT_CLASS.invoice
    case 'receipt': return ATTACHMENT_CLASS.receipt
    case 'spreadsheet': case 'sheet': case 'excel': return ATTACHMENT_CLASS.spreadsheet
    case 'presentation': case 'slide': case 'deck': return ATTACHMENT_CLASS.presentation
    case 'document': case 'doc': return ATTACHMENT_CLASS.document
    case 'other': return ATTACHMENT_CLASS.other
    default: return null
  }
}

/**
 * What class one document is.
 *
 * The server's word wins, always — it read the file. The fallback below only ever runs for a row
 * that has not been classified yet (every attachment created before #277, until its text is
 * extracted and it is re-announced), and it deliberately guesses only SHAPES: pdf, image,
 * spreadsheet, presentation, document. It will never call something an invoice or a receipt, because
 * those live in the words inside the file and this side has never seen them — a guess there would
 * put a stranger's quote in the user's Receipts.
 */
export function attachmentClass(doc) {
  const stated = parseAttachmentClass(doc?.class)
  if (stated) return stated

  const ext = fileExtension(doc?.filename)
  const type = String(doc?.contentType || '').split(';')[0].trim().toLowerCase()
  if (ext === 'pdf' || type === 'application/pdf') return ATTACHMENT_CLASS.pdf
  if (isImageAttachment({ contentType: doc?.contentType, extension: ext })) return ATTACHMENT_CLASS.image
  if (['xls', 'xlsx', 'xlsm', 'ods', 'csv', 'tsv', 'numbers'].includes(ext)) return ATTACHMENT_CLASS.spreadsheet
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return ATTACHMENT_CLASS.presentation
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'pages'].includes(ext)) return ATTACHMENT_CLASS.document
  return ATTACHMENT_CLASS.other
}

/** How many attachments the counts describe, whatever the server called the field. */
export function attachmentsTotal(counts) {
  const total = Number(counts?.total)
  return Number.isFinite(total) && total > 0 ? total : 0
}

/**
 * Is this record the ATTACHMENT counts, rather than one of the other count shapes the Briefing is
 * handed (the mailbox dashboard, the waiting endpoint)?
 *
 * The strip takes "whatever counts the caller holds" in one argument and tells them apart by shape,
 * so the shapes have to be distinguishable. `receipts` alone cannot do it — the mailbox dashboard
 * counts receipts too — so this asks for a numeric `total` or one of the classes only a file can be
 * in. A record of all zeroes still IS this shape: "nothing attached" is an answer, and it is a
 * different thing from never having asked.
 */
export function isAttachmentCounts(counts) {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return false
  const named = ['total', 'documents', 'pdfs', 'images', 'invoices', 'spreadsheets', 'presentations']
  return named.some(key => Number.isFinite(Number(counts[key])) && counts[key] !== null && counts[key] !== '')
}

/**
 * "12 Attachments" / "1 Attachment" — the row's words, and the breadcrumb's.
 *
 * Capitalised after the count on EVERY card (busy-my-web#609, Saffet 2026-10-09: "Everything listed
 * in a briefing should have a capital letter after a Number"). #603 made the capital an option for
 * the Email Briefing card alone; the story briefing's row was the only lower-case caller, and it is
 * a Briefing row too, so the option is gone. Both callers are Briefing rows — the rail/timeline
 * cards count their files elsewhere and keep their own lower case.
 */
export function attachmentsLabel(total) {
  return countLabel(total, 'Attachment')
}

/**
 * The Briefing row for the entry, or null when there is nothing to say.
 *
 * Null rather than a "0 attachments" row, for the reason lib/briefings/rows.js drops empty rows: a
 * briefing states what IS there, and a zero row reads as a fault. It is also a FILTER row like every
 * other count row, so the strip renders and closes it with no new template — `active` marks it, and
 * the class chips live underneath (see AttachmentsPanel.vue).
 */
export function attachmentsRow(counts, { active = false } = {}) {
  const total = attachmentsTotal(counts)
  if (!total) return null
  const full = attachmentsLabel(total)
  return {
    key: ATTACHMENTS_FILTER,
    icon: ATTACHMENTS_ICON,
    // An active row drops its count the way every other kind row does (lib/briefings/story.js's
    // `bareCount`) — the list underneath is already saying how many there are.
    text: active ? 'Attachments' : full,
    full,
    filter: ATTACHMENTS_FILTER,
    active: !!active,
  }
}

/**
 * The chips under the row: every class the user actually HAS, in the design's order, with the one in
 * force marked.
 *
 * A class with no files is dropped for the same reason an empty row is: a "Receipts 0" chip is an
 * invitation to an empty list. The chip in force is kept even at 0 so that clearing a filter is
 * always possible from the chip that set it.
 */
export function attachmentChips(counts, activeClass = null) {
  const active = parseAttachmentClass(activeClass)
  return ATTACHMENT_CHIPS
    .map(chip => ({ ...chip, count: Number(counts?.[chip.countKey]) || 0, active: chip.key === active }))
    .filter(chip => chip.count > 0 || chip.active)
}

/**
 * Clicking a chip. Clicking the one already on turns it OFF (back to every attachment) — the same
 * toggle every filter row in the Briefing does, so the chips need no separate "All" chip to escape
 * through.
 */
export function nextAttachmentClass(current, clicked) {
  const now = parseAttachmentClass(current)
  const next = parseAttachmentClass(clicked)
  if (!next) return null
  return now === next ? null : next
}

/** A byte count as a person reads it. '' when the server sent none — better than "0 B" on a file. */
export function attachmentSize(octets) {
  const bytes = Number(octets)
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`
}

/**
 * The documents as the file explorer's list already draws them
 * (components/FileExplorer/FileExplorerItem.vue takes `{ name, kind, size, lastModified }`).
 *
 * Reusing that row rather than writing a third list of files is the point: the explorer, the
 * entity's chips and this entry are all "the user's files", and three different renderings of the
 * same document is how three panes end up disagreeing about what it is called. The extra fields
 * (`id`, `class`, `contentType`) ride along for the preview, which opens by id.
 */
export function attachmentEntries(docs, className = null) {
  const only = parseAttachmentClass(className)
  return (docs || [])
    .filter(d => d && d.id)
    .map(d => ({
      id: String(d.id),
      name: String(d.filename || '').trim() || 'Attachment',
      kind: 'File',
      size: Number(d.octets) || 0,
      sizeLabel: attachmentSize(d.octets),
      lastModified: d.updateOn || d.createdOn || null,
      contentType: d.contentType || '',
      timelineId: d.timelineId || null,
      // WHO the file came from, for the listing's "Related email" column (busy-my-web#482, frame
      // 40011000:380432). busy-main-bff names the first non-caller sender on the documents listing
      // since 2026-09-15; `timelineHeadLine` (the subject) is the fallback the column had before.
      relatedEmail: String(d.relatedEmail || '').trim(),
      // …and the ADDRESS behind that name, plus which way the file went (busy-main-bff#43,
      // 2026-09-15 parity audit): the attachment rail's frames say "Attachment sent / To: …" over
      // one and "Attachment received / From" over the other, and neither could be told apart from
      // a display name alone. Both are OPTIONAL — the listing predates them, so a row without them
      // carries '' and '' and the rail falls back to the received wording it has always shown.
      senderAddress: String(d.senderAddress || '').trim(),
      direction: String(d.direction || '').trim().toLowerCase(),
      timelineHeadLine: String(d.timelineHeadLine || '').trim(),
      class: attachmentClass(d),
    }))
    .filter(e => !only || e.class === only)
}

/** Counted from the documents themselves — the same shape the BFF's briefing endpoint returns, for
 * the surfaces that already hold the list and should not ask again. */
export function countAttachments(docs) {
  const entries = attachmentEntries(docs)
  const of = (key) => entries.filter(e => e.class === key).length
  return {
    documents: of(ATTACHMENT_CLASS.document),
    pdfs: of(ATTACHMENT_CLASS.pdf),
    images: of(ATTACHMENT_CLASS.image),
    invoices: of(ATTACHMENT_CLASS.invoice),
    receipts: of(ATTACHMENT_CLASS.receipt),
    spreadsheets: of(ATTACHMENT_CLASS.spreadsheet),
    presentations: of(ATTACHMENT_CLASS.presentation),
    // …and the files that are none of those (busy-my-web#528). The chips never drew this one — a
    // chip labelled "Other" invites a click into a list nobody can describe — but the TYPE ROWS
    // state what the pile is MADE OF, and a breakdown whose lines do not add up to the total is
    // worse than a line called Other. It is also where a video lands today: busy-document-srv has
    // no video class (busy-document-srv#7's list is document / pdf / image / invoice / receipt /
    // spreadsheet / presentation / other), so a clip is an "other" file until it does.
    other: of(ATTACHMENT_CLASS.other),
    total: entries.length,
  }
}

/**
 * The row for everything the classifier could not name, which the chip row deliberately has none
 * of. Its glyph is the entry's own paperclip: it is not a KIND of file, it is the absence of one.
 */
const OTHER_TYPE = { key: ATTACHMENT_CLASS.other, label: 'Other', countKey: 'other', icon: ATTACHMENTS_ICON }

/**
 * THE BRIEFING'S ATTACHMENTS ENTRY, AS TYPES (busy-my-web#528).
 *
 * Saffet, 2026-09-17: "The documents have to be removed from the Briefing — just the type of
 * folders pdf, jpeg, video, whatever the file is word — and amount."
 *
 * So the entry lists one row per FILE TYPE with its count — "PDFs 12", "Images 4", "Documents 3"
 * — and no per-document rows at all. The documents themselves are not gone; they are one click
 * away, in the listing that was already written for them (the Email Workspace's attachments
 * listing, busy-my-web#403), and a type row opens that listing NARROWED to its own class.
 *
 * The types are busy-document-srv's own classes, not a vocabulary invented here, because the row
 * has to be openable: narrowing is `documents?class=…`, and a class this side made up is a class
 * the server cannot be asked for. That is why a VIDEO — which Saffet names — has no row of its
 * own yet and is counted under Other; giving it one means adding the class to busy-document-srv
 * first (noted on the issue), and until then a Video row would open an empty list.
 *
 * Counts come from whichever answer the caller holds: the briefing's own per-class counts
 * (`documents/briefing`, which stay whole while a class filter narrows the LIST), or, where those
 * have not landed, the documents themselves. Same rows either way — one shaping, so the entry
 * cannot say two different things about one mailbox.
 *
 * @param {Array<object>|object|null} source  the documents, or the per-class counts
 * @param {{active?: string|null}} opts       the class in force, kept even at zero so the filter it
 *                                            set is always clearable from the row that set it
 * @returns {Array<{key:string,label:string,count:number,icon:string,active:boolean}>}
 */
export function attachmentTypeRows(source, { active = null } = {}) {
  const counts = withOther(Array.isArray(source) ? countAttachments(source) : (source || {}))
  const on = parseAttachmentClass(active)
  return [...ATTACHMENT_CHIPS, OTHER_TYPE]
    .map(type => ({
      key: type.key,
      label: type.label,
      icon: type.icon,
      count: Number(counts?.[type.countKey]) || 0,
      active: type.key === on,
    }))
    .filter(row => row.count > 0 || row.active)
}

/**
 * The counts with an `other` on them, worked out where the server did not send one.
 *
 * `documents/briefing` answers the seven classes and a total, not the leftovers, so "other" has to
 * be the arithmetic: the total less everything that was named. Floored at zero — the total and the
 * class counts are one answer, but a payload that disagrees with itself must not put a negative row
 * on the card — and left exactly as it arrived when the server DOES name it.
 */
function withOther(counts) {
  if (counts?.other !== null && counts?.other !== undefined && counts?.other !== '') return counts
  const named = ATTACHMENT_CHIPS.reduce((sum, chip) => sum + (Number(counts?.[chip.countKey]) || 0), 0)
  return { ...counts, other: Math.max(0, attachmentsTotal(counts) - named) }
}

/** "12 files" — what a type row's count says on its own, for a title or an aria label. */
export function attachmentTypeCountLabel(count) {
  const n = Number(count) || 0
  return `${n} file${n === 1 ? '' : 's'}`
}

/**
 * The document SOURCES of an ask answer, split off the mail ones (busy-main-bff's `AskSource.type`).
 *
 * The BFF defaults `type` to "email" precisely so a source with no type is still a mail thread —
 * so the split is by what a source SAYS it is, never by what it happens to carry.
 */
export function askDocumentSources(sources) {
  return (sources || []).filter(s => s && String(s.type || 'email').toLowerCase() === 'document')
}

/** …and the mail half, which is every source that is not a document. */
export function askEmailSources(sources) {
  return (sources || []).filter(s => s && String(s.type || 'email').toLowerCase() !== 'document')
}

/**
 * WHICH renderer opens this file (components/Attachments/*).
 *
 * The per-type renderers have existed for a long time, and so has this question — asked, and
 * answered slightly differently, inside SearchPanel.vue, TimelinePanel.vue and
 * EntityPlaceholders/index.vue, each with its own `isPdfAttachment`. This entry would have been the
 * fourth copy. It is one function instead, returning a KEY the panel maps to a component, so the
 * decision is testable without mounting anything and the three older copies have somewhere to
 * converge on later.
 *
 * Decided from the FILE NAME first, then the content type — the same order the older copies use, and
 * the right one: an attachment's extension is what survives being mailed around, while the content
 * type is whatever the sending client happened to claim.
 */
export function previewRendererFor(file) {
  const name = String(file?.fileName || file?.filename || file?.name || '').toLowerCase()
  const type = String(file?.contentType || '').split(';')[0].trim().toLowerCase()
  const ext = fileExtension(name)

  if (ext === 'pdf' || type === 'application/pdf') return 'pdf'
  if (isImageAttachment({ contentType: type, extension: ext })) return 'image'
  if (['mp3', 'wav', 'm4a', 'ogg', 'oga', 'aac', 'flac'].includes(ext) || type.startsWith('audio/')) return 'audio'
  if (ext === 'eml' || type === 'message/rfc822') return 'eml'
  if (['csv', 'tsv'].includes(ext) || type === 'text/csv') return 'csv'
  if (['ppt', 'pptx'].includes(ext)) return 'ppt'
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md'].includes(ext)) return 'document'
  return 'default'
}

/**
 * A list entry as the preview store wants an attachment: `{ id, fileName, contentType, octets }`
 * (lib/store/modules/attachmentPreviews.js). The renderers download by id, so nothing else is
 * needed — and nothing else should be invented, since a field this side made up would be a field
 * the renderer trusts.
 */
export function previewAttachment(entry) {
  return {
    id: String(entry?.id || ''),
    fileName: String(entry?.name || entry?.filename || '').trim() || 'Attachment',
    contentType: entry?.contentType || '',
    octets: Number(entry?.size ?? entry?.octets) || 0,
    isDraft: false,
  }
}

/** One answer-source document as a chip: the name, its glyph, and the id the preview opens. */
export function askDocumentChip(source) {
  const name = String(source?.filename || source?.subject || '').trim() || 'Attachment'
  const cls = attachmentClass({ class: source?.class, filename: name, contentType: source?.contentType })
  return {
    id: source?.documentId ? String(source.documentId) : '',
    name,
    class: cls,
    icon: attachmentClassIcon(cls),
    label: attachmentClassLabel(cls),
  }
}

export default attachmentsRow
